/**
 * Shared frame for the learn pages (framing V3, V10).
 *
 * One H1, breadcrumb, lede, article body, then the audience acceptance
 * from the framing: a jill reader reaches a working example in one click
 * ("Open this example in the tool"), and the other learn pages and docs
 * are linked both ways.
 */

import type { ReactNode } from 'react';
import Link from '../Link';
import { LEARN_ROUTE_LIST, type PublicRoute } from '../../lib/route';
import './LearnPage.css';

interface LearnLayoutProps {
  route: PublicRoute;
  /** H1 — normally the route title */
  heading: string;
  lede: string;
  children: ReactNode;
  /** Label for the closing call to action */
  ctaLabel?: string;
}

export default function LearnLayout({ route, heading, lede, children, ctaLabel = 'Open this example in the tool' }: LearnLayoutProps) {
  const others = LEARN_ROUTE_LIST.filter((r) => r.path !== route.path);
  return (
    <main className="learn">
      <article className="learn-article">
        <nav className="learn-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">›</span>
          <Link to="/learn/time-distance-diagram" event="learn-open" source="learn">Learn</Link>
          <span aria-hidden="true">›</span>
          <span>{route.learn?.label ?? route.label}</span>
        </nav>
        <header className="learn-header">
          <h1>{heading}</h1>
          <p className="learn-lede">{lede}</p>
          <p className="learn-meta">
            <time dateTime={route.updated}>Updated {route.updated}</time> · SigTDD
          </p>
        </header>

        {children}

        <aside className="learn-cta" aria-label="Try it">
          <h2>Try it yourself</h2>
          <p>
            The workspace opens with an example corridor loaded, so the diagram on this page is one click
            away. Nothing to install; a free account is only needed to save your own corridors.
          </p>
          <Link to="/app" className="learn-btn">{ctaLabel}</Link>
        </aside>

        <nav className="learn-related" aria-label="Related pages">
          <h2>Related</h2>
          <ul>
            {others.map((r) => (
              <li key={r.path}>
                <Link to={r.path} event="learn-open" source="learn">{r.label}</Link>
                <span>{r.learn?.summary}</span>
              </li>
            ))}
            <li>
              <Link to="/docs" event="docs-open" source="learn">Documentation</Link>
              <span>Every control in the tool, import and export formats, FAQ.</span>
            </li>
          </ul>
        </nav>
      </article>
    </main>
  );
}
